

import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useTranslations } from "next-intl";
import { translateBackendError } from "@/lib/i18n/translateBackendError";
import { useUserLocale } from "@/hooks/useUserLocale";
import { GuestNameButton } from "@/components/PGuestDetails/GuestNameButton";
import { useSlottingStore, selectTimezone } from "@/lib/slotting/store";
import { useQueryClient } from "@tanstack/react-query";
import { invalidateOfferingData } from "@/lib/slotting/invalidateSlotting";
import {
  createAppointment,
  updateAppointment,
  updateAppointmentStatus as updateAppointmentStatusApi,
  cancelAppointment,
  dismissWarning,
  searchGuestsForSlotting,
  getGuestCompanions,
  getGuestById,
  getWaitlist,
  joinWaitlist,
  cancelWaitlistEntry,
  promoteFromWaitlist,
} from "@/lib/slotting/api";
import { formatTime, toMinutesFromMidnight, localMinutesToIso, getTranslatedWarningMessage } from "@/lib/slotting/utils";
import { formatDateInTz } from "@/lib/utils/datetime-tz";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { SHEET_INITIAL, SHEET_ANIMATE, SHEET_EXIT, SHEET_SPRING } from "@/components/Modal/sheetMotion";
import type {
  Appointment,
  AppointmentGuest,
  AppointmentStatus,
  WarningType,
  ComputedWarning,
  WaitlistEntry,
} from "@/lib/slotting/types";
import type { Guest } from "@/types/Guest";
import type { SlottingModalContext } from "@/lib/slotting/modalContext";
import { IconX, IconSearch } from "@tabler/icons-react";
import { FormInput } from "@/components/Inputs/FormInput";
import { MultilineInput } from "@/components/Inputs/MultilineInput";
import { OptionInput } from "@/components/Inputs/OptionInput";
import { TableCellContentDate } from "@/components/TableCell/components/TCCDate";
import { PrimaryButton } from "@/components/Buttons/PrimaryButton";
import { SecondaryButton } from "@/components/Buttons/SecondaryButton";
import { LoadingSpinner } from "@/components/LoadingSpinner/LoadingSpinner";
import { useOnboarding } from "@/providers/OnboardingProvider";
import { useSuppressCoach } from "@/lib/onboarding/useSuppressCoach";
import { useCapabilities } from "@/providers/CapabilitiesProvider";
import { useBackdropDismiss } from "@/lib/a11y/useBackdropDismiss";

type Mode = "create" | "edit";

type GuestEntry = {
  guestId: string;
  name: string;
  email?: string;
  isCompanion: boolean;
  companionOfId?: string;
  companionOfName?: string;
};

type Props = {
  mode: Mode;
  eventId: string;
  initialLaneId?: string;
  initialDate?: string;
  initialStartMinutes?: number;
  initialEndMinutes?: number;
  initialGuestId?: string;
  initialTreatmentId?: string;
  initialNotes?: string;
  appointment?: Appointment;
  /** Explicit offering context for callers outside the slotting board (e.g.
   *  the guest-detail popup). When set, store reads are overridden and store
   *  writes are skipped unless the board currently shows the same offering. */
  context?: SlottingModalContext;
  /** Overlay z-class — callers stacked above the board's z ladder raise it. */
  zClass?: string;
  onClose: () => void;
  onCreated?: () => void;
};

export function PSlottingAppointment({
  mode,
  eventId,
  initialLaneId,
  initialDate,
  initialStartMinutes,
  initialEndMinutes,
  initialGuestId,
  initialTreatmentId,
  initialNotes,
  appointment,
  context,
  zClass = "z-[80]",
  onClose,
  onCreated,
}: Props) {
  const t = useTranslations("slotting");
  const backdrop = useBackdropDismiss(onClose);
  const tErrors = useTranslations("errors");
  const queryClient = useQueryClient();
  const { completeStep } = useOnboarding();
  useSuppressCoach(); // hide the bottom-right coach so it can't cover this modal's buttons
  const { can } = useCapabilities();
  const canWrite = can("slotting:write");
  const locale = useUserLocale();
  const storeOffering = useSlottingStore((s) => s.offering);
  const storeLanes = useSlottingStore((s) => s.lanes);
  const storeTreatments = useSlottingStore((s) => s.treatments);
  const addAppointmentToStore = useSlottingStore((s) => s.addAppointment);
  const updateAppointmentInStore = useSlottingStore((s) => s.updateAppointmentInStore);
  const removeAppointmentFromStore = useSlottingStore((s) => s.removeAppointmentFromStore);
  const storeTimezone = useSlottingStore(selectTimezone);

  const offering = context?.offering ?? storeOffering;
  const lanes = context?.lanes ?? storeLanes;
  const treatments = context?.treatments ?? storeTreatments;
  const timezone = context?.timezone ?? storeTimezone;

  const offeringId = offering?.offeringId ?? "";

  // In context mode only mirror writes into the board store when the board
  // currently shows this same offering (idempotent live-update); a foreign
  // offering must never leak into the board's state.
  const shouldWriteStore = () =>
    !context || useSlottingStore.getState().offering?.offeringId === offeringId;

  // Form state
  const [laneId, setLaneId] = useState(
    appointment?.laneIds[0] ?? initialLaneId ?? lanes[0]?.laneId ?? ""
  );
  const [date, setDate] = useState(() => {
    if (appointment) {
      // Extract the local date in the offering's timezone
      const d = new Date(appointment.startDt);
      const parts = new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
      return parts; // en-CA formats as YYYY-MM-DD
    }
    // Default to TODAY in the offering tz (was browser-UTC today → could
    // pre-select yesterday/tomorrow near midnight for a far-offset admin).
    return initialDate ?? formatDateInTz(new Date().toISOString(), "en-CA", timezone);
  });
  const [startMinutes, setStartMinutes] = useState(() => {
    if (appointment) {
      return toMinutesFromMidnight(appointment.startDt, timezone);
    }
    return initialStartMinutes ?? 9 * 60;
  });
  const [treatmentId, setTreatmentId] = useState(appointment?.treatmentId ?? initialTreatmentId ?? "");
  const [endMinutesOverride, setEndMinutesOverride] = useState<number | null>(
    initialEndMinutes ?? null
  );
  const [customEndTime, setCustomEndTime] = useState(initialEndMinutes != null);
  const [notes, setNotes] = useState(appointment?.notes ?? initialNotes ?? "");
  const [status, setStatus] = useState<AppointmentStatus>(appointment?.status ?? offering?.defaultAppointmentStatus ?? "CONFIRMED");
  const [guests, setGuests] = useState<GuestEntry[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attemptedSave, setAttemptedSave] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [showDurationPrompt, setShowDurationPrompt] = useState(false);
  const [pendingTreatmentId, setPendingTreatmentId] = useState("");

  // Guest search
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<Guest[]>([]);
  const [searching, setSearching] = useState(false);
  const searchTimeout = useRef<ReturnType<typeof setTimeout>>(undefined);
  const laneRef = useRef<HTMLInputElement>(null);
  const startTimeRef = useRef<HTMLInputElement>(null);
  const endTimeRef = useRef<HTMLInputElement>(null);
  const treatmentRef = useRef<HTMLInputElement>(null);
  const statusRef = useRef<HTMLInputElement>(null);

  // Companion prompt
  const [companionPrompt, setCompanionPrompt] = useState<{
    guest: GuestEntry;
    companions: Guest[];
  } | null>(null);

  // Waitlist state (edit mode only)
  const [waitlistEntries, setWaitlistEntries] = useState<WaitlistEntry[]>([]);
  const [waitlistNames, setWaitlistNames] = useState<Map<string, string>>(new Map());
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [showWaitlistSearch, setShowWaitlistSearch] = useState(false);
  const [waitlistSearchTerm, setWaitlistSearchTerm] = useState("");
  const [waitlistSearchResults, setWaitlistSearchResults] = useState<Guest[]>([]);
  const [waitlistSearching, setWaitlistSearching] = useState(false);
  const waitlistSearchTimeout = useRef<ReturnType<typeof setTimeout>>(undefined);

  const selectedLane = useMemo(
    () => lanes.find((l) => l.laneId === laneId),
    [lanes, laneId]
  );

  const laneTreatments = useMemo(() => {
    if (!selectedLane) return treatments;
    const laneTreatmentIds = selectedLane.treatmentIds ?? [];
    if (laneTreatmentIds.length === 0) return treatments;
    return treatments.filter((t) =>
      laneTreatmentIds.includes(t.treatmentId)
    );
  }, [selectedLane, treatments]);

  const selectedTreatment = useMemo(
    () => treatments.find((t) => t.treatmentId === treatmentId),
    [treatments, treatmentId]
  );

  // Calculate end time
  const endMinutes = useMemo(() => {
    if (endMinutesOverride !== null) return endMinutesOverride;
    const duration = selectedTreatment?.durationMinutes ?? 60;
    return startMinutes + duration;
  }, [startMinutes, endMinutesOverride, selectedTreatment]);

  // Snapshot of initial values for dirty-checking (edit mode)
  const initialSnapshot = useRef<{
    laneId: string;
    date: string;
    startMinutes: number;
    endMinutes: number | null;
    treatmentId: string;
    notes: string;
    status: AppointmentStatus;
    guestIds: string[];
  } | null>(null);
  if (mode === "edit" && appointment && initialSnapshot.current === null) {
    const initStart = toMinutesFromMidnight(appointment.startDt, timezone);
    const initEnd = toMinutesFromMidnight(appointment.endDt, timezone);
    const treatDuration = treatments.find(
      (t) => t.treatmentId === (appointment.treatmentId ?? "")
    )?.durationMinutes ?? 60;
    initialSnapshot.current = {
      laneId: appointment.laneIds[0] ?? "",
      date: new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(appointment.startDt)),
      startMinutes: initStart,
      endMinutes: initEnd !== initStart + treatDuration ? initEnd : null,
      treatmentId: appointment.treatmentId ?? "",
      notes: appointment.notes ?? "",
      status: appointment.status,
      guestIds: [...appointment.guests.map((g) => g.guestId)].sort(),
    };
  }

  // Load existing appointment guests
  useEffect(() => {
    if (mode === "edit" && appointment) {
      // Seed names from the display name the appointment payload already
      // carries (server-resolved, scope-honored); fall back to the id stub only
      // when it's absent.
      // Carry the parent link over too. It is what makes a companion row readable
      // ("Begleitung von …") AND what removeGuest cascades on — rebuilt without it,
      // deleting a main guest silently left its companions behind in the saved
      // appointment. The server resolves both (companions are booked as their own
      // rows, so the link lives on the guest record, not on the appointment).
      const fallbackEntries: GuestEntry[] = appointment.guests.map((g) => ({
        guestId: g.guestId,
        name: g.guestDisplayName || t("modalGuestFallback", { id: g.guestId.slice(-6) }),
        isCompanion: g.isCompanion,
        companionOfId: g.companionOf ?? undefined,
        companionOfName: g.companionOfDisplayName ?? undefined,
      }));
      setGuests(fallbackEntries);

      // Only fetch names the payload didn't already provide.
      const needsFetch = appointment.guests.filter((g) => !g.guestDisplayName);
      if (needsFetch.length > 0) {
        Promise.all(
          needsFetch.map((g) => getGuestById(eventId, g.guestId))
        ).then((results) => {
          const resolved = new Map<string, string>();
          results.forEach((guest, i) => {
            if (!guest) return;
            const parts = [guest.firstName, guest.lastName].filter(Boolean);
            const name = parts.length > 0 ? parts.join(" ") : guest.email || "";
            if (name) resolved.set(needsFetch[i].guestId, name);
          });
          if (resolved.size === 0) return;
          setGuests((prev) =>
            prev.map((entry) =>
              resolved.has(entry.guestId)
                ? { ...entry, name: resolved.get(entry.guestId)! }
                : entry
            )
          );
        });
      }

      if (appointment.endDt) {
        const apptEnd = toMinutesFromMidnight(appointment.endDt, timezone);
        const expectedEnd =
          startMinutes + (selectedTreatment?.durationMinutes ?? 60);
        if (apptEnd !== expectedEnd) {
          setEndMinutesOverride(apptEnd);
          setCustomEndTime(true);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pre-fill guest from request selection
  useEffect(() => {
    if (mode === "create" && initialGuestId && guests.length === 0) {
      const fallbackName = t("modalGuestFallback", { id: initialGuestId.slice(-6) });
      setGuests([{ guestId: initialGuestId, name: fallbackName, isCompanion: false }]);
      getGuestById(eventId, initialGuestId).then((guest) => {
        if (!guest) return;
        const parts = [guest.firstName, guest.lastName].filter(Boolean);
        const name = parts.length > 0 ? parts.join(" ") : guest.email || fallbackName;
        setGuests((prev) =>
          prev.map((e) => e.guestId === initialGuestId ? { ...e, name } : e)
        );
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Guest search with debounce
  useEffect(() => {
    if (searchTerm.length < 3) {
      setSearchResults([]);
      return;
    }
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(async () => {
      setSearching(true);
      try {
        const results = await searchGuestsForSlotting(eventId, searchTerm);
        setSearchResults(results);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => {
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
    };
  }, [searchTerm, eventId]);

  // Fetch waitlist entries in edit mode
  const fetchWaitlist = useCallback(async () => {
    if (mode !== "edit" || !appointment || !offeringId) return;
    setWaitlistLoading(true);
    try {
      const entries = await getWaitlist(eventId, offeringId, appointment.appointmentId);
      setWaitlistEntries(entries);
      // Prefer the display name each entry already carries; only look up the ones
      // that don't have one.
      const names = new Map<string, string>();
      entries.forEach((e) => {
        if (e.guestDisplayName) names.set(e.guestId, e.guestDisplayName);
      });
      const needsFetch = entries.filter((e) => !e.guestDisplayName);
      if (needsFetch.length > 0) {
        const results = await Promise.all(needsFetch.map((e) => getGuestById(eventId, e.guestId)));
        results.forEach((guest, i) => {
          if (guest) {
            const parts = [guest.firstName, guest.lastName].filter(Boolean);
            names.set(needsFetch[i].guestId, parts.length > 0 ? parts.join(" ") : guest.email || needsFetch[i].guestId.slice(-6));
          }
        });
      }
      setWaitlistNames(names);
    } catch {
      // Silently fail
    } finally {
      setWaitlistLoading(false);
    }
  }, [mode, appointment, offeringId, eventId]);

  useEffect(() => {
    fetchWaitlist();
  }, [fetchWaitlist]);

  // Waitlist guest search with debounce
  useEffect(() => {
    if (waitlistSearchTerm.length < 3) {
      setWaitlistSearchResults([]);
      return;
    }
    if (waitlistSearchTimeout.current) clearTimeout(waitlistSearchTimeout.current);
    waitlistSearchTimeout.current = setTimeout(async () => {
      setWaitlistSearching(true);
      try {
        const results = await searchGuestsForSlotting(eventId, waitlistSearchTerm);
        setWaitlistSearchResults(results);
      } catch {
        setWaitlistSearchResults([]);
      } finally {
        setWaitlistSearching(false);
      }
    }, 300);
    return () => {
      if (waitlistSearchTimeout.current) clearTimeout(waitlistSearchTimeout.current);
    };
  }, [waitlistSearchTerm, eventId]);

  const handleJoinWaitlist = async (guest: Guest) => {
    if (!guest.guestId || !appointment || !offeringId) return;
    // Block if guest is already assigned to this appointment
    if (guests.some((g) => g.guestId === guest.guestId)) return;
    // Block if guest is already on the waitlist
    if (waitlistEntries.some((e) => e.guestId === guest.guestId)) return;
    try {
      await joinWaitlist(eventId, offeringId, appointment.appointmentId, guest.guestId);
      setShowWaitlistSearch(false);
      setWaitlistSearchTerm("");
      setWaitlistSearchResults([]);
      await fetchWaitlist();
    } catch {
      // Ignore
    }
  };

  const handleRemoveWaitlistEntry = async (entry: WaitlistEntry) => {
    if (!appointment || !offeringId) return;
    try {
      await cancelWaitlistEntry(eventId, offeringId, appointment.appointmentId, entry.waitlistEntryId);
      await fetchWaitlist();
    } catch {
      // Ignore
    }
  };

  const handlePromoteNext = async () => {
    if (!appointment || !offeringId) return;
    try {
      await promoteFromWaitlist(eventId, offeringId, appointment.appointmentId);
      invalidateOfferingData(queryClient, eventId, offeringId);
      await fetchWaitlist();
    } catch {
      // Ignore
    }
  };

  const guestName = (g: Guest) => {
    const parts = [g.firstName, g.lastName].filter(Boolean);
    if (parts.length > 0) return parts.join(" ");
    if (g.email) return g.email;
    return t("modalGuestFallback", { id: g.guestId?.slice(-6) ?? "" });
  };

  const addGuest = useCallback(
    async (guest: Guest) => {
      if (!guest.guestId) return;
      if (guests.some((g) => g.guestId === guest.guestId)) return;
      // Block if guest is already on the waitlist for this appointment
      if (waitlistEntries.some((e) => e.guestId === guest.guestId)) return;

      const capacity = selectedTreatment?.capacity ?? 1;
      if (capacity === 1 && guests.filter((g) => !g.isCompanion).length >= 1) {
        return;
      }

      const entry: GuestEntry = {
        guestId: guest.guestId,
        name: guestName(guest),
        email: guest.email,
        isCompanion: false,
      };
      setGuests((prev) => [...prev, entry]);
      setSearchTerm("");
      setSearchResults([]);

      // Check for companions
      if ((guest.numCompanions ?? 0) > 0) {
        try {
          const companions = await getGuestCompanions(eventId, guest.guestId);
          if (companions.length > 0) {
            setCompanionPrompt({ guest: entry, companions });
          }
        } catch {
          // Ignore companion fetch errors
        }
      }
    },
    [guests, waitlistEntries, selectedTreatment, eventId]
  );

  const addCompanions = useCallback(
    (parentEntry: GuestEntry, companions: Guest[]) => {
      const newEntries: GuestEntry[] = companions
        .filter((c) => c.guestId && !guests.some((g) => g.guestId === c.guestId))
        .map((c) => ({
          guestId: c.guestId!,
          name: guestName(c),
          email: c.email,
          isCompanion: true,
          companionOfId: parentEntry.guestId,
          companionOfName: parentEntry.name,
        }));
      setGuests((prev) => [...prev, ...newEntries]);
      setCompanionPrompt(null);
    },
    [guests]
  );

  const removeGuest = useCallback((guestId: string) => {
    setGuests((prev) => {
      const filtered = prev.filter(
        (g) => g.guestId !== guestId && g.companionOfId !== guestId
      );
      return filtered;
    });
  }, []);

  // Time options in 5min increments — matches the calendar's 5-min snap so
  // dragged/resized 5-min starts and ends display and are selectable here.
  const timeOptions = useMemo(() => {
    const opts: { value: number; label: string }[] = [];
    for (let m = 0; m < 24 * 60; m += 5) {
      opts.push({ value: m, label: formatTime(m) });
    }
    return opts;
  }, []);

  const laneLabels = useMemo(() => lanes.map((l) => l.name), [lanes]);
  const selectedLaneLabel = useMemo(() => {
    const lane = lanes.find((l) => l.laneId === laneId);
    if (!lane) return "";
    return lane.name;
  }, [lanes, laneId]);

  const timeOptionLabels = useMemo(() => timeOptions.map((o) => o.label), [timeOptions]);
  const selectedStartLabel = useMemo(() => timeOptions.find((o) => o.value === startMinutes)?.label ?? "", [timeOptions, startMinutes]);
  const selectedEndLabel = useMemo(() => timeOptions.find((o) => o.value === endMinutes)?.label ?? "", [timeOptions, endMinutes]);

  // COMPLETED / NO_SHOW are intentionally not offered — nothing auto-transitions
  // into them and the product does not use them, so they are kept out of the
  // status picker. The enum still defines them for backward safety.
  const STATUS_KEYS: { value: AppointmentStatus; tKey: string }[] = [
    { value: "TENTATIVE", tKey: "statusTentative" },
    { value: "CONFIRMED", tKey: "statusConfirmed" },
    { value: "CHECKED_IN", tKey: "statusCheckedIn" },
    { value: "CANCELLED", tKey: "statusCancelled" },
  ];
  const statusLabels = useMemo(() => STATUS_KEYS.map((s) => t(s.tKey as Parameters<typeof t>[0])), [t]);
  const statusLabelMap = useMemo(() => {
    const map = new Map<string, AppointmentStatus>();
    STATUS_KEYS.forEach((s, i) => map.set(statusLabels[i], s.value));
    return map;
  }, [statusLabels]);
  const selectedStatusLabel = useMemo(() => {
    const idx = STATUS_KEYS.findIndex((s) => s.value === status);
    return idx >= 0 ? statusLabels[idx] : statusLabels[0];
  }, [status, statusLabels]);

  const treatmentLabels = useMemo(() => [t("modalNone"), ...laneTreatments.map((tr) => t("modalTreatmentDuration", { name: tr.name, duration: tr.durationMinutes }))], [laneTreatments, t]);
  const selectedTreatmentLabel = useMemo(() => {
    if (!treatmentId) return t("modalNone");
    const tr = laneTreatments.find((tr) => tr.treatmentId === treatmentId);
    return tr ? t("modalTreatmentDuration", { name: tr.name, duration: tr.durationMinutes }) : t("modalNone");
  }, [laneTreatments, treatmentId, t]);

  // Build ISO datetime strings (timezone-aware)
  const buildDt = (dateStr: string, minutes: number) =>
    localMinutesToIso(dateStr, minutes, timezone);

  // Dirty check: has the form changed from its initial state?
  const hasChanges = useMemo(() => {
    if (mode === "create") return true;
    const snap = initialSnapshot.current;
    if (!snap) return true;
    if (snap.laneId !== laneId) return true;
    if (snap.date !== date) return true;
    if (snap.startMinutes !== startMinutes) return true;
    if (snap.endMinutes !== endMinutesOverride) return true;
    if (snap.treatmentId !== treatmentId) return true;
    if (snap.notes !== notes) return true;
    if (snap.status !== status) return true;
    const currentGuestIds = [...guests.map((g) => g.guestId)].sort();
    if (snap.guestIds.length !== currentGuestIds.length) return true;
    if (snap.guestIds.some((id, i) => id !== currentGuestIds[i])) return true;
    return false;
  }, [mode, laneId, date, startMinutes, endMinutesOverride, treatmentId, notes, status, guests]);

  // --- Live Warnings ---
  const liveWarnings = useMemo(() => {
    const warns: ComputedWarning[] = [];
    if (!selectedLane) return warns;

    const startDt = buildDt(date, startMinutes);
    const endDt = buildDt(date, endMinutes);

    // Capacity/buffer/cross-offering warnings need the board's full
    // appointment set — in context mode the store belongs to a different (or
    // no) offering, so skip them; the server validates authoritatively on
    // save and those error codes are already handled below.
    const store = context ? null : useSlottingStore.getState();

    // CAPACITY_EXCEEDED
    if (store) {
      const cap = store.getCapacityForSlot(laneId, startDt, endDt, treatmentId);
      const currentGuestCount =
        mode === "edit"
          ? guests.length
          : cap.used + guests.length;
      if (currentGuestCount > cap.total) {
        warns.push({
          type: "CAPACITY_EXCEEDED",
          severity: "error",
          message: t("warningCapacityExceeded", { used: currentGuestCount, total: cap.total }),
          dismissed: false,
        });
      }
    }

    // OUTSIDE_AVAILABILITY
    // Compare the window's day in the event timezone, not its raw UTC date —
    // otherwise windows near local midnight match the wrong calendar day.
    const tzDay = (iso: string) =>
      new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(iso));
    const dayAvail = (selectedLane.availability ?? []).filter(
      (a) => tzDay(a.startDt) === date
    );
    if (dayAvail.length > 0) {
      const inAvail = dayAvail.some(
        (a) => a.startDt <= startDt && a.endDt >= endDt
      );
      if (!inAvail) {
        warns.push({
          type: "OUTSIDE_AVAILABILITY",
          severity: "warning",
          message: t("warningOutsideAvailability"),
          dismissed: false,
        });
      }
    }

    // BUFFER_TIME_VIOLATION
    if (store && selectedLane.bufferMinutes > 0) {
      const laneAppts = store.getAppointmentsForLane(laneId, date);
      const bufferViolation = laneAppts.some((a) => {
        if (mode === "edit" && a.appointmentId === appointment?.appointmentId)
          return false;
        const aEnd = new Date(a.endDt).getTime();
        const thisStart = new Date(startDt).getTime();
        const aStart = new Date(a.startDt).getTime();
        const thisEnd = new Date(endDt).getTime();
        const bufferMs = selectedLane.bufferMinutes * 60000;
        return (
          (aEnd > thisStart - bufferMs && aEnd <= thisStart) ||
          (thisEnd > aStart - bufferMs && thisEnd <= aStart)
        );
      });
      if (bufferViolation) {
        warns.push({
          type: "BUFFER_TIME_VIOLATION",
          severity: "warning",
          message: t("warningBufferViolation", { minutes: selectedLane.bufferMinutes }),
          dismissed: false,
        });
      }
    }

    // MISSING_TREATMENT
    if (
      treatmentId &&
      (selectedLane.treatmentIds?.length ?? 0) > 0 &&
      !selectedLane.treatmentIds!.includes(treatmentId)
    ) {
      warns.push({
        type: "MISSING_TREATMENT",
        severity: "error",
        message: t("warningMissingTreatment"),
        dismissed: false,
      });
    }

    // CROSS_OFFERING_CONFLICT
    if (store && guests.length > 0) {
      const guestIds = new Set(guests.map((g) => g.guestId));
      const conflicts = store.appointments.filter((a) => {
        if (mode === "edit" && a.appointmentId === appointment?.appointmentId)
          return false;
        if (a.status === "CANCELLED") return false;
        if (a.startDt >= endDt || a.endDt <= startDt) return false;
        return a.guests.some((ag) => guestIds.has(ag.guestId));
      });
      if (conflicts.length > 0) {
        warns.push({
          type: "CROSS_OFFERING_CONFLICT",
          severity: "warning",
          message: t("warningOverlapping", { count: conflicts.length }),
          dismissed: false,
        });
      }
    }

    return warns;
  }, [
    selectedLane,
    date,
    startMinutes,
    endMinutes,
    laneId,
    treatmentId,
    guests,
    mode,
    appointment,
    context,
  ]);

  // Treatment change handler
  const handleTreatmentChange = (newTreatmentId: string) => {
    if (customEndTime && newTreatmentId) {
      const newTreatment = treatments.find(
        (t) => t.treatmentId === newTreatmentId
      );
      if (newTreatment) {
        setPendingTreatmentId(newTreatmentId);
        setShowDurationPrompt(true);
        return;
      }
    }
    setTreatmentId(newTreatmentId);
    if (!customEndTime) {
      setEndMinutesOverride(null);
    }
  };

  const handleDurationPromptKeep = () => {
    setTreatmentId(pendingTreatmentId);
    setShowDurationPrompt(false);
  };

  const handleDurationPromptAdjust = () => {
    setTreatmentId(pendingTreatmentId);
    setEndMinutesOverride(null);
    setCustomEndTime(false);
    setShowDurationPrompt(false);
  };

  const handleEndTimeChange = (minutes: number) => {
    setEndMinutesOverride(minutes);
    setCustomEndTime(true);
  };

  // A live warning blocks save when:
  //  • CAPACITY_EXCEEDED + offering doesn't allow overbooking, OR
  //  • MISSING_TREATMENT (lane doesn't support the chosen treatment) — backend
  //    rejects this regardless, so block client-side too.
  // Other severity="warning" entries are visible but soft-allow.
  const blockingWarnings = liveWarnings.filter((w) => {
    if (w.type === "CAPACITY_EXCEEDED") return offering?.overbookingPct === 0;
    if (w.type === "MISSING_TREATMENT") return true;
    return false;
  });
  const hasBlockingWarning = blockingWarnings.length > 0;
  const laneMissing = !laneId;
  const hasValidationErrors = !offeringId || laneMissing || hasBlockingWarning;

  // Save
  const handleSave = async () => {
    if (hasValidationErrors) {
      setAttemptedSave(true);
      if (
        offering?.overbookingPct === 0 &&
        liveWarnings.some((w) => w.type === "CAPACITY_EXCEEDED")
      ) {
        setError(t("errorCapacityOverbooking"));
      }
      return;
    }

    setSaving(true);
    setError(null);

    const startDt = buildDt(date, startMinutes);
    const endDt = buildDt(date, endMinutes);

    const apptGuests: AppointmentGuest[] = guests.map((g) => ({
      guestId: g.guestId,
      isCompanion: g.isCompanion,
      checkInAt: null,
    }));

    try {
      if (mode === "create") {
        const created = await createAppointment(eventId, offeringId, {
          laneIds: [laneId],
          treatmentId: treatmentId || undefined,
          startDt,
          endDt,
          guests: apptGuests,
          notes: notes || undefined,
          status,
        });
        invalidateOfferingData(queryClient, eventId, offeringId);
        if (shouldWriteStore()) addAppointmentToStore(created);
        completeStep("create_first_appointment");
        onCreated?.();
      } else if (appointment) {
        const updated = await updateAppointment(
          eventId,
          offeringId,
          appointment.appointmentId,
          {
            laneIds: [laneId],
            treatmentId: treatmentId || undefined,
            startDt,
            endDt,
            guests: apptGuests,
            notes: notes || undefined,
          }
        );
        invalidateOfferingData(queryClient, eventId, offeringId);
        // Update status if changed
        if (status !== appointment.status) {
          const withStatus = await updateAppointmentStatusApi(
            eventId,
            offeringId,
            appointment.appointmentId,
            status,
          );
          if (shouldWriteStore()) updateAppointmentInStore(withStatus);
        } else {
          if (shouldWriteStore()) updateAppointmentInStore(updated);
        }
      }
      onClose();
    } catch (e: unknown) {
      const code = e instanceof Error ? e.message : "";
      if (code === "slotBooked" || code === "appointmentOutsideAvailability") {
        setError(t(code === "slotBooked" ? "errorSlotBooked" : "errorOutsideAvailability"));
      } else if (code === "appointmentInBlockedTime") {
        setError(t("errorInBlockedTime"));
      } else if (code === "guestConflictAcrossOfferings") {
        setError(t("errorGuestConflict"));
      } else {
        setError(translateBackendError(tErrors, e instanceof Error ? e : "", t("errorSaveFailed")));
      }
    } finally {
      setSaving(false);
    }
  };

  // Cancel
  const handleCancel = async () => {
    if (!appointment || !offeringId) return;
    setSaving(true);
    try {
      await cancelAppointment(
        eventId,
        offeringId,
        appointment.appointmentId,
        cancelReason || t("cancelReasonDefault")
      );
      invalidateOfferingData(queryClient, eventId, offeringId);
      if (shouldWriteStore()) removeAppointmentFromStore(appointment.appointmentId);
      onClose();
    } catch (e: unknown) {
      setError(translateBackendError(tErrors, e instanceof Error ? e : "", t("errorCancelFailed")));
    } finally {
      setSaving(false);
    }
  };

  // Dismiss warning
  const handleDismissWarning = async (warningType: WarningType) => {
    if (!appointment || !offeringId) return;
    try {
      const updated = await dismissWarning(
        eventId,
        offeringId,
        appointment.appointmentId,
        warningType
      );
      if (shouldWriteStore()) updateAppointmentInStore(updated);
    } catch {
      // Ignore
    }
  };

  // Capacity gauge
  const mainGuestCount = guests.filter((g) => !g.isCompanion).length;
  const companionCount = guests.filter((g) => g.isCompanion).length;
  const totalGuestCount = guests.length;
  const capacity = selectedTreatment?.capacity ?? 1;

  // Offering date range for the date picker — as tz-local WALL dates so the min/
  // max match the appointment's wall date. A raw UTC `.split("T")[0]` on the
  // offering start/end instants drifts a day at the boundary in a non-UTC tz.
  const minDate = offering?.startDt
    ? formatDateInTz(offering.startDt, "en-CA", timezone)
    : undefined;
  const maxDate = offering?.endDt
    ? formatDateInTz(offering.endDt, "en-CA", timezone)
    : undefined;

  return createPortal(
    <div className={`fixed inset-0 ${zClass} flex items-end sm:items-center justify-center bg-black/50 sm:pr-[var(--ai-rail-push)] transition-[padding] duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none`} {...backdrop}>
      <motion.div
        className="surface-primary rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-lg max-h-[90dvh] overflow-hidden flex flex-col"
        initial={SHEET_INITIAL}
        animate={SHEET_ANIMATE}
        exit={SHEET_EXIT}
        transition={SHEET_SPRING}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Inner scroll container — keeps the scrollbar inside the rounded
            corners so the right-side rounding isn't clipped by the scrollbar. */}
        <div className="overflow-y-auto min-h-0">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4">
          <h2 className="h3 font-primary">
            {mode === "create" ? t("modalNewAppointment") : t("modalEditAppointment")}
          </h2>
          <button onClick={onClose} className="font-tertiary hover:font-primary transition-colors">
            <IconX size={20} />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          {/* Lane */}
          <OptionInput
            inputRef={laneRef}
            label={t("modalLane")}
            options={laneLabels}
            defaultValue={selectedLaneLabel}
            required={true}
            disabled={!canWrite}
            error={
              attemptedSave && laneMissing
                ? tErrors("slottingLaneRequired")
                : undefined
            }
            setParent={(displayName) => {
              const lane = lanes.find((l) => l.name === displayName);
              if (lane) setLaneId(lane.laneId);
            }}
            labelMargin="mb-1"
          />

          {/* Treatment */}
          <OptionInput
            inputRef={treatmentRef}
            label={t("modalTreatment")}
            options={treatmentLabels}
            sortOptions={true}
            defaultValue={selectedTreatmentLabel}
            required={false}
            disabled={!canWrite}
            setParent={(displayName) => {
              if (displayName === t("modalNone")) {
                handleTreatmentChange("");
                return;
              }
              const tr = laneTreatments.find((tr) => t("modalTreatmentDuration", { name: tr.name, duration: tr.durationMinutes }) === displayName);
              if (tr) handleTreatmentChange(tr.treatmentId);
            }}
            labelMargin="mb-1"
            updateOptions={true}
          />

          {/* Date */}
          <div>
            <label className="body-2-medium font-primary mb-2 block">{t("modalDate")}</label>
            <TableCellContentDate
              editable={canWrite}
              isSelected={true}
              isEditing={canWrite}
              value={date ? `${date}T00:00:00.000Z` : ""}
              onChangeValue={(v) => setDate(v ? v.split("T")[0] : "")}
              minDate={minDate ? `${minDate}T00:00:00.000Z` : undefined}
              maxDate={maxDate ? `${maxDate}T00:00:00.000Z` : undefined}
            />
          </div>

          {/* Start / End Time */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <OptionInput
              inputRef={startTimeRef}
              label={t("modalStartTime")}
              options={timeOptionLabels}
              defaultValue={selectedStartLabel}
              required={true}
              disabled={!canWrite}
              setParent={(label) => {
                const opt = timeOptions.find((o) => o.label === label);
                if (opt) setStartMinutes(opt.value);
              }}
              labelMargin="mb-1"
            />
            <div>
              <OptionInput
                inputRef={endTimeRef}
                label={`${t("modalEndTime")}${customEndTime ? ` ${t("modalCustom")}` : ""}`}
                options={timeOptionLabels.filter((_, i) => timeOptions[i].value > startMinutes)}
                defaultValue={selectedEndLabel}
                required={true}
                disabled={!canWrite}
                setParent={(label) => {
                  const opt = timeOptions.find((o) => o.label === label);
                  if (opt) handleEndTimeChange(opt.value);
                }}
                labelMargin="mb-1"
                updateOptions={true}
              />
            </div>
          </div>

          {/* Guests */}
          <div>
            <label className="block body-2-medium font-secondary mb-1">
              {t("modalGuests")}
            </label>

            {/* Capacity gauge */}
            {capacity > 1 && (
              <div className="mb-2 body-4-regular font-tertiary">
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 surface-tertiary rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${
                        totalGuestCount > capacity
                          ? "surface-font-error"
                          : totalGuestCount === capacity
                            ? "surface-status-amber"
                            : "surface-font-success"
                      }`}
                      style={{
                        width: `${Math.min((totalGuestCount / capacity) * 100, 100)}%`,
                      }}
                    />
                  </div>
                  <span>
                    {totalGuestCount}/{capacity}
                  </span>
                </div>
                {companionCount > 0 && (
                  <span className="mt-0.5 block">
                    {t("modalGuestCount", { mainCount: mainGuestCount, companionCount, totalCount: totalGuestCount, capacity })}
                  </span>
                )}
              </div>
            )}

            {/* Search input */}
            {canWrite && (
            <div className="relative">
              <FormInput
                id="modal-guest-search"
                search
                value={searchTerm}
                onChangeAction={setSearchTerm}
                placeholder={t("modalSearchGuests")}
                icon={IconSearch}
                width="w-full"
                trailingContent={searching ? <LoadingSpinner /> : undefined}
              />
              {searchResults.length > 0 && (
                <div className="absolute z-10 w-full mt-1 surface-primary border outline-primary rounded-md shadow-lg max-h-48 overflow-y-auto">
                  {searchResults.map((g) => (
                    <button
                      key={g.guestId}
                      onClick={() => addGuest(g)}
                      disabled={
                        guests.some((eg) => eg.guestId === g.guestId) ||
                        waitlistEntries.some((e) => e.guestId === g.guestId)
                      }
                      className="w-full text-left px-3 py-2 body-3-regular hover:surface-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="font-primary">
                        {guestName(g)}
                      </span>
                      {g.email && (
                        <span className="ml-2 font-tertiary body-4-regular">
                          {g.email}
                        </span>
                      )}
                      {(g.numCompanions ?? 0) > 0 && (
                        <span className="ml-2 body-4-regular chip-purple-fg">
                          {t("modalCompanions", { count: g.numCompanions ?? 0 })}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
            )}

            {/* Guest list */}
            {guests.length > 0 && (
              <div className="mt-2 space-y-1">
                {guests.map((g) => (
                  <div
                    key={g.guestId}
                    className={`flex items-center justify-between px-2 py-1.5 rounded body-3-regular ${
                      g.isCompanion
                        ? // Indent only under a parent that is actually in this list —
                          // otherwise the row reads as a child of whichever guest happens
                          // to be above it. It stays purple either way (it IS a companion),
                          // and the "companion of …" caption carries the affiliation.
                          `chip-purple-bg ${
                            g.companionOfId && guests.some((x) => x.guestId === g.companionOfId)
                              ? "ml-4"
                              : ""
                          }`
                        : "surface-secondary"
                    }`}
                  >
                    <div className="truncate">
                      <span className="font-primary">
                        {/* Context mode = launched from a guest-detail popup
                            that sits BELOW this overlay; opening another popup
                            would stack it invisibly underneath. */}
                        {context ? (
                          g.name
                        ) : (
                          <GuestNameButton guestId={g.guestId}>{g.name}</GuestNameButton>
                        )}
                      </span>
                      {g.isCompanion && g.companionOfName && (
                        <span className="ml-1 body-4-regular chip-purple-fg">
                          {t("modalCompanionOf", { name: g.companionOfName })}
                        </span>
                      )}
                    </div>
                    {canWrite && (
                      <button
                        onClick={() => removeGuest(g.guestId)}
                        className="font-tertiary hover:font-error ml-2 shrink-0"
                      >
                        <IconX size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Waitlist (edit mode only) */}
          {mode === "edit" && appointment && (
            <div>
              <label className="block body-2-medium font-secondary mb-1">
                {t("waitlistSection")}
              </label>

              {waitlistLoading ? (
                <div className="body-4-regular font-tertiary py-2">{t("guestPanelLoading")}</div>
              ) : waitlistEntries.length === 0 ? (
                <p className="body-4-regular font-tertiary">{t("waitlistEmpty")}</p>
              ) : (
                <div className="space-y-1">
                  {waitlistEntries.map((entry) => (
                    <div
                      key={entry.waitlistEntryId}
                      className="flex items-center justify-between px-2 py-1.5 rounded body-3-regular surface-secondary"
                    >
                      <div className="flex items-center gap-2 truncate">
                        <span className="body-4-regular font-tertiary">#{entry.position}</span>
                        <span className="font-primary">
                          {waitlistNames.get(entry.guestId) || entry.guestId.slice(-6)}
                        </span>
                      </div>
                      {canWrite && (
                        <button
                          onClick={() => handleRemoveWaitlistEntry(entry)}
                          className="font-tertiary hover:font-error ml-2 shrink-0"
                          title={t("waitlistRemove")}
                        >
                          <IconX size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {canWrite && (
                <div className="flex gap-2 mt-2">
                  {waitlistEntries.length > 0 && (
                    <button
                      onClick={handlePromoteNext}
                      className="body-4-medium px-2 py-1 rounded surface-blue font-blue hover:opacity-80"
                    >
                      {t("waitlistPromoteNext")}
                    </button>
                  )}
                  <button
                    onClick={() => setShowWaitlistSearch(!showWaitlistSearch)}
                    className="body-4-medium px-2 py-1 rounded surface-tertiary font-secondary hover:opacity-80"
                  >
                    {t("waitlistAddGuest")}
                  </button>
                </div>
              )}

              {showWaitlistSearch && (
                <div className="relative mt-2">
                  <FormInput
                    id="modal-waitlist-search"
                    search
                    value={waitlistSearchTerm}
                    onChangeAction={setWaitlistSearchTerm}
                    placeholder={t("modalSearchGuests")}
                    icon={IconSearch}
                    width="w-full"
                    trailingContent={waitlistSearching ? <LoadingSpinner /> : undefined}
                  />
                  {waitlistSearchResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 surface-primary border outline-primary rounded-md shadow-lg max-h-48 overflow-y-auto">
                      {waitlistSearchResults.map((g) => (
                        <button
                          key={g.guestId}
                          onClick={() => handleJoinWaitlist(g)}
                          disabled={
                            guests.some((eg) => eg.guestId === g.guestId) ||
                            waitlistEntries.some((e) => e.guestId === g.guestId)
                          }
                          className="w-full text-left px-3 py-2 body-3-regular hover:surface-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="font-primary">{guestName(g)}</span>
                          {g.email && (
                            <span className="ml-2 font-tertiary body-4-regular">{g.email}</span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Notes */}
          <MultilineInput
            id="modal-notes"
            label={t("modalNotes")}
            value={notes}
            onChangeAction={setNotes}
            rows={2}
            width="w-full"
            height="min-h-16"
            disabled={!canWrite}
          />

          {/* Status */}
          <OptionInput
            inputRef={statusRef}
            label={t("modalStatus")}
            options={statusLabels}
            defaultValue={selectedStatusLabel}
            required={true}
            disabled={!canWrite}
            setParent={(label) => {
              const val = statusLabelMap.get(label);
              if (val) setStatus(val);
            }}
            labelMargin="mb-1"
          />

          {/* Live Warnings */}
          {liveWarnings.length > 0 && (
            <div className="space-y-2">
              {liveWarnings.map((w, i) => (
                <WarningBanner key={`${w.type}-${i}`} warning={w} />
              ))}
            </div>
          )}

          {/* Server warnings with dismiss (edit mode) */}
          {mode === "edit" && appointment?.computedWarnings && appointment.computedWarnings.length > 0 && (
            <div className="space-y-2">
              <p className="body-4-bold font-tertiary uppercase tracking-wider">
                {t("modalServerWarnings")}
              </p>
              {appointment.computedWarnings.map((w, i) => {
                const dismissed = appointment.dismissedWarnings?.some(
                  (d) => d.type === w.type
                );
                const dismissedInfo = appointment.dismissedWarnings?.find(
                  (d) => d.type === w.type
                );
                return (
                  <div
                    key={`server-${w.type}-${i}`}
                    className={`flex items-center justify-between rounded-lg px-3 py-2 body-3-regular ${
                      dismissed
                        ? "surface-tertiary font-quaternary"
                        : w.severity === "error"
                          ? "hint-red-bg hint-red-font"
                          : w.severity === "warning"
                            ? "hint-orange-bg hint-orange-font"
                            : "surface-blue font-blue"
                    }`}
                  >
                    <div>
                      <span>{getTranslatedWarningMessage(w.type, w.message, t)}</span>
                      {dismissed && dismissedInfo && (
                        <span className="block body-4-regular font-quaternary mt-0.5">
                          {t("modalDismissedAt", { dateTime: new Date(dismissedInfo.dismissedAt).toLocaleString(locale) })}
                        </span>
                      )}
                    </div>
                    {!dismissed && canWrite && (
                      <button
                        onClick={() =>
                          handleDismissWarning(w.type as WarningType)
                        }
                        className="body-4-medium px-2 py-1 rounded-md surface-tertiary hover:surface-secondary font-secondary ml-2 shrink-0"
                      >
                        {t("modalDismiss")}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="rounded-lg border hint-red-outline hint-red-bg hint-red-font px-3 py-2 body-3-medium">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 space-y-2">
          <div className="flex gap-2">
            <SecondaryButton
              text={t("modalClose")}
              onClick={onClose}
              width="w-full"
            />
            {canWrite && (
              <PrimaryButton
                text={saving ? t("modalSaving") : mode === "create" ? t("modalCreate") : t("modalSave")}
                onClick={handleSave}
                disabled={
                  saving ||
                  !hasChanges ||
                  (attemptedSave && hasValidationErrors)
                }
                loading={saving}
                width="w-full"
              />
            )}
          </div>
          {canWrite && mode === "edit" && (
            <SecondaryButton
              text={t("modalCancelAppointment")}
              onClick={() => setShowCancelConfirm(true)}
              disabled={saving}
              textColor="font-error"
              borderColor=""
              width="w-full"
            />
          )}
        </div>
        </div>
      </motion.div>

      {/* Duration prompt dialog */}
      {showDurationPrompt && (
        <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/30 sm:pr-[var(--ai-rail-push)] transition-[padding] duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none" onClick={(e) => { if (e.target === e.currentTarget) setShowDurationPrompt(false); }}>
          <div className="surface-primary rounded-2xl shadow-2xl p-6 max-w-sm mx-4" onClick={(e) => e.stopPropagation()}>
            <p className="body-2-regular font-primary mb-4">
              {t("modalDurationPrompt", { minutes: treatments.find((tr) => tr.treatmentId === pendingTreatmentId)?.durationMinutes ?? 0 })}
            </p>
            <div className="flex gap-2 justify-end">
              <SecondaryButton text={t("modalKeepCustom")} onClick={handleDurationPromptKeep} size="xsmall" width="w-auto" />
              <PrimaryButton text={t("modalAdjust")} onClick={handleDurationPromptAdjust} size="xsmall" width="w-auto" />
            </div>
          </div>
        </div>
      )}

      {/* Companion prompt */}
      {companionPrompt && (
        <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/30 sm:pr-[var(--ai-rail-push)] transition-[padding] duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none" onClick={(e) => { if (e.target === e.currentTarget) setCompanionPrompt(null); }}>
          <div className="surface-primary rounded-2xl shadow-2xl p-6 max-w-sm mx-4" onClick={(e) => e.stopPropagation()}>
            <p className="body-2-regular font-primary mb-4">
              {t("modalAddCompanions", { name: companionPrompt.guest.name, count: companionPrompt.companions.length })}
            </p>
            <div className="flex gap-2 justify-end">
              <SecondaryButton text={t("modalNo")} onClick={() => setCompanionPrompt(null)} size="xsmall" width="w-auto" />
              <PrimaryButton text={t("modalYes")} onClick={() => addCompanions(companionPrompt.guest, companionPrompt.companions)} size="xsmall" width="w-auto" />
            </div>
          </div>
        </div>
      )}

      {/* Cancel confirmation dialog */}
      {showCancelConfirm && (
        <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/30 sm:pr-[var(--ai-rail-push)] transition-[padding] duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none" onClick={(e) => { if (e.target === e.currentTarget) setShowCancelConfirm(false); }}>
          <div className="surface-primary rounded-2xl shadow-2xl p-6 max-w-sm mx-4" onClick={(e) => e.stopPropagation()}>
            <p className="body-2-medium font-primary mb-3">
              {t("modalCancelConfirm")}
            </p>
            <MultilineInput
              id="modal-cancel-reason"
              value={cancelReason}
              onChangeAction={setCancelReason}
              placeholder={t("modalReasonPlaceholder")}
              rows={2}
              width="w-full"
              height="min-h-16"
              margin="mt-0 mb-3"
            />
            <div className="flex gap-2 justify-end">
              <SecondaryButton text={t("modalKeep")} onClick={() => setShowCancelConfirm(false)} size="xsmall" width="w-auto" />
              <PrimaryButton text={saving ? t("modalCancelling") : t("modalCancelAppointment")} onClick={handleCancel} disabled={saving} loading={saving} size="xsmall" width="w-auto" color="surface-error-dark" />
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
}

// --- Warning Banner ---

function WarningBanner({ warning }: { warning: ComputedWarning }) {
  const t = useTranslations("slotting");
  const colors =
    warning.severity === "error"
      ? "hint-red-bg hint-red-font hint-red-outline"
      : warning.severity === "warning"
        ? "hint-orange-bg hint-orange-font hint-orange-outline"
        : "surface-blue font-blue outline-blue";

  return (
    <div className={`rounded-md border px-3 py-2 body-3-regular ${colors}`}>
      {getTranslatedWarningMessage(warning.type, warning.message, t)}
    </div>
  );
}
